import React from 'react';
import { AlertLogEntry } from './AlertHistoryPanel';
import { fmtFreqUnit } from './behaviorTypes';

interface AlertReportModalProps {
  alert: AlertLogEntry;
  onClose: () => void;
  onAcknowledge: (id: string) => void;
}

const AlertReportModal: React.FC<AlertReportModalProps> = ({ alert, onClose, onAcknowledge }) => {
  const formatDateTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'spin': return '🔄';
      case 'toilet': return '🚽';
      case 'rest': return '😴';
      case 'activity': return '📉';
      case 'scratch': return '✋';
      case 'lying': return '😴';
      case 'sitting': return '🪑';
      case 'standing': return '🧍';
      case 'walk': return '🚶';
      default: return '⚠️';
    }
  };

  const getBehaviorDescription = (type: string) => {
    switch (type) {
      case 'spin':
        return 'Excessive spinning behavior detected. This may indicate anxiety, neurological issues, or compulsive disorders. Monitor frequency and duration.';
      case 'toilet':
        return 'Frequent toilet visits detected. This could indicate digestive issues, urinary problems, or anxiety-related behavior.';
      case 'rest':
        return 'Prolonged rest period detected. While rest is normal, extended periods without movement may warrant attention.';
      case 'activity':
        return 'Significant drop in activity level detected. This could indicate pain, illness, or environmental factors affecting behavior.';
      case 'scratch':
        return 'Excessive scratching detected. This may indicate allergies, fleas, skin irritation, or anxiety. Monitor frequency and check for skin issues.';
      case 'lying':
        return 'Prolonged lying detected. While rest is normal, excessive lying (>2h) may indicate lethargy, pain, or depression.';
      case 'sitting':
        return 'Prolonged sitting detected. Extended sitting may indicate stiffness, reluctance to move, or discomfort.';
      case 'standing':
        return 'Extended standing detected. Prolonged standing may indicate discomfort, anxiety, or reluctance to lie down.';
      case 'walk':
        return 'High walking frequency detected. Excessive walking or pacing may indicate anxiety, restlessness, or neurological issues.';
      default:
        return 'Behavioral anomaly detected.';
    }
  };

  const getMetricLabel = (metricType: string) => {
    switch (metricType) {
      case 'frequency': return alert.frequencyWindow ? fmtFreqUnit(alert.frequencyWindow) : 'events/hr';
      case 'duration': return 'minutes';
      case 'activity': return '%';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-gray-200 dark:border-claude-darkBorder ${
          alert.severity === 'critical' 
            ? 'bg-red-50 dark:bg-red-900/20' 
            : 'bg-yellow-50 dark:bg-yellow-900/20'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{getIcon(alert.type)}</span>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
                  {alert.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Alert Report
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Severity Badge */}
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              alert.severity === 'critical'
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
            }`}>
              {alert.severity === 'critical' ? '🔴 CRITICAL' : '🟡 WARNING'}
            </span>
            {alert.acknowledged && (
              <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                ✓ Acknowledged
              </span>
            )}
          </div>

          {/* Alert Details */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Detected At
              </h3>
              <p className="text-gray-900 dark:text-claude-darkText">
                {formatDateTime(alert.detectedAt)}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Alert Message
              </h3>
              <p className="text-gray-900 dark:text-claude-darkText">
                {alert.message}
              </p>
            </div>

            {alert.threshold !== undefined && alert.actualValue !== undefined && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Threshold Comparison
                </h3>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Metric: <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{alert.metricType}</span>
                  {alert.metricType && (
                    <span> ({getMetricLabel(alert.metricType)})</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 dark:bg-claude-darkBg rounded-lg">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Threshold</div>
                    <div className="text-xl font-bold text-gray-900 dark:text-claude-darkText">
                      {alert.threshold}
                      <span className="text-xs font-normal text-gray-500 ml-1">{getMetricLabel(alert.metricType)}</span>
                    </div>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Actual Value</div>
                    <div className="text-xl font-bold text-red-600 dark:text-red-400">
                      {alert.actualValue}
                      <span className="text-xs font-normal text-red-400 ml-1">{getMetricLabel(alert.metricType)}</span>
                    </div>
                  </div>
                </div>
                {/* Visual bar showing threshold vs actual */}
                <div className="mt-3">
                  <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`absolute top-0 left-0 h-full rounded-full ${
                        alert.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${Math.min(100, (alert.actualValue / alert.threshold) * 100)}%` }}
                    />
                    <div
                      className="absolute top-0 h-full w-0.5 bg-gray-900 dark:bg-white"
                      style={{ left: '100%' }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0</span>
                    <span className="font-medium text-gray-600 dark:text-gray-300">
                      Threshold: {alert.threshold}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Behavior Analysis
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {getBehaviorDescription(alert.type)}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                Contact Notification
              </h3>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                alert.contactNotified
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
              }`}>
                {alert.contactNotified ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Contact has been notified
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Contact not notified
                  </>
                )}
              </div>
            </div>

            {/* Recommendation */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                    Recommendation
                  </h4>
                  <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                    {alert.severity === 'critical'
                      ? 'This alert requires immediate attention. Consider consulting a veterinarian if behavior persists.'
                      : 'Monitor the situation. If behavior continues or worsens, investigate potential causes.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-claude-darkBorder flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-claude-darkSurfaceHover rounded-lg transition-colors"
          >
            Close
          </button>
          {!alert.acknowledged && (
            <button
              onClick={() => {
                onAcknowledge(alert.id);
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
            >
              Acknowledge Alert
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertReportModal;
